import submit from "submit-form";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDraggable, useDroppable } from "@dnd-kit/react";
import SvgIcon from "../../../../../parts/SvgIcon";
import { CopyComponent } from "../../../../../parts/CopyComponent";
import { PasteMealComponent } from "../../../../../parts/PasteMealComponent";
import { isValidDate } from "../../../../../utils/nutrition-util";
import { FoodItem } from "../shared/FoodItem";
import { GroupedMealContainer } from "../shared/GroupedMealContainer";
import { EmptyMealState } from "../shared/EmptyMealState";
import {
  getMealDraggableId,
  getMealDroppableId,
  getMealDragData,
} from "../utils/meal-drag-drop-helpers";

export const MealPlannerCalendar = ({
  query,
  meal,
  day,
  week,
  viewMode,
  basicMealsInformation,
  planMode,
}) => {
  const {
    macrosTotal = {
      calories: 0,
      carbs: 0,
      fats: 0,
      fibers: 0,
      proteins: 0,
    },
    foods,
    type,
  } = meal;
  const {
    calories: totalCalories,
    carbs: totalCarbs,
    fats: totalFats,
    proteins: totalProteins,
  } = macrosTotal;
  const hasMacrosTotal =
    totalProteins !== 0 ||
    totalCarbs !== 0 ||
    totalFats !== 0 ||
    totalCalories !== 0;

  const { t } = useTranslation(["athlete-portal", "common"]);
  const { date, hasData, dayType, optionId } = day;
  const { mealPlan, athlete } = query;

  const dateToUse = isValidDate(date)
    ? date
    : t("athlete-portal:mealPlannerCalendar.weekLabel", { week, date });

  // Build query params for modal (include dayType and optionId if present for simplified)
  // Use manual string construction to preserve spaces in dateToUse (URLSearchParams converts spaces to +)
  let modalHref = `/actions/refresh?modal[type]=create-meal-planner-food&mealPlannerDate=${encodeURIComponent(dateToUse)}&mealPlannerMeal=${encodeURIComponent(type)}`;
  // For simplified meal plans
  if (dayType && optionId) {
    modalHref += `&dayType=${encodeURIComponent(dayType)}&optionId=${encodeURIComponent(optionId.toString())}`;
  }

  const hasFoods = Array.isArray(foods) && foods.length > 0;

  // Get current planMode (default to "detailed" if not provided)
  const currentPlanMode = planMode || "detailed";

  /**
   * DRAG (Dragging):
   * - Allows this meal to be DRAGGED to another meal
   * - Only works if the meal has foods (hasFoods) and is not in viewMode
   * - refDraggable: Applied directly to day-nutrition (the container that can be dragged)
   * - handleRefDraggable: Applied to the drag icon (the visual "handle" that the user grabs)
   * - attributes/listeners: Events and attributes necessary for dragging (applied to drag handle)
   * - isDragging: Boolean indicating if this meal is currently being dragged
   */
  const {
    ref: refDraggable,
    handleRef: handleRefDraggable,
    attributes,
    listeners,
    isDragging,
  } = useDraggable({
    id: getMealDraggableId(currentPlanMode, day, type),
    disabled: viewMode || !hasFoods,
    data: getMealDragData(currentPlanMode, day, type, week),
  });

  /**
   * DROP (Dropping/Receiving):
   * - Allows this meal to RECEIVE another dragged meal
   * - Always works (even if the meal is empty) except in viewMode
   * - refDroppable: Applied to the container that can receive drops (the div.day)
   * - isOver: Indicates if there is an element being dragged over this droppable
   */
  const droppableId = getMealDroppableId(currentPlanMode, day, type);
  const { ref: refDroppable, isDropTarget } = useDroppable({
    id: droppableId,
    disabled: viewMode,
    data: getMealDragData(currentPlanMode, day, type, week),
  });

  /**
   * Group foods by meal or single foods
   */
  const groupFoodsByMealId = (foods) => {
    if (!Array.isArray(foods)) return { grouped: [], singles: [] };

    const mealInfoMap = new Map(
      basicMealsInformation?.map((m) => [String(m._id), m]) || [],
    );

    const groupedMap = new Map();
    const singles = [];

    for (const food of foods) {
      const mealId = food.mealId ? String(food.mealId) : null;

      // Food without mealdId (it is single food)
      if (!mealId) {
        singles.push(food);
        continue;
      }

      // Check if mealId exists in basicMealsInformation
      const mealInfo = mealInfoMap.get(mealId);

      if (!mealInfo) {
        // Doesn't exists (it is single food)
        singles.push(food);
        continue;
      }

      // Group
      if (!groupedMap.has(mealId)) {
        groupedMap.set(mealId, {
          meal: mealInfo,
          foods: [],
          totals: {
            proteins: 0,
            carbs: 0,
            fats: 0,
            calories: 0,
          },
        });
      }

      const group = groupedMap.get(mealId);

      group.foods.push(food);

      // --- accumulate macros ---
      if (food.foodId) {
        const { proteins = 0, carbs = 0, fats = 0, calories = 0 } = food.foodId;

        group.totals.proteins += proteins;
        group.totals.carbs += carbs;
        group.totals.fats += fats;
        group.totals.calories += calories;
      }
    }

    return {
      grouped: [...groupedMap.values()],
      singles,
    };
  };

  /**
   * Delete all the foods of a meal of a day
   */
  const deleteFoodsOfMeal = () => {
    if (
      process.browser &&
      confirm(t("athlete-portal:mealPlannerCalendar.removeAllFoodsConfirm"))
    ) {
      const currentPlanMode = planMode || "detailed";
      const mealPlanId = query.mealPlan;

      // Build body based on planMode
      let body = {};
      if (currentPlanMode === "simplified" && dayType && optionId) {
        // Simplified meal plan: use dayType and optionId
        body = {
          planMode: "simplified",
          dayType: dayType, // "training" or "rest"
          optionId: optionId, // Stable ID of the option
          meal: type,
        };
      } else {
        // Detailed meal plan: use weekIndex and date
        body = {
          indexWeek: week - 1,
          date,
          meal: type,
        };
      }

      submit(
        `/actions/meal-planner/remove-foods-of-meal?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  const { grouped, singles } = groupFoodsByMealId(foods);

  // Hide copy/drag on items when this slot has only one item (avoids duplicate icons)
  const onlyOneSingle = (singles?.length ?? 0) === 1 && grouped.length === 0;
  const onlyOneGroup = grouped.length === 1 && (singles?.length ?? 0) === 0;

  return (
    <div className="meal-planner-calendar">
      {hasData ? (
        // DROP ZONE: This container can RECEIVE dragged meals
        // - refDroppable makes this div a valid drop area
        // - Works even if the meal is empty (allows dropping on empty meals)
        <div
          className={`day ${hasFoods ? "day-full" : "day-empty"} ${viewMode ? "day-no-hover" : ""} ${isDropTarget ? "day-drop-over" : ""}`}
          ref={refDroppable}
        >
          {!hasFoods ? (
            <EmptyMealState
              date={dateToUse}
              mealType={type}
              disabled={viewMode}
              isDropTarget={isDropTarget}
              dayType={dayType}
              optionId={optionId}
              query={query}
              meal={meal}
              day={day}
              planMode={currentPlanMode}
              week={week}
            />
          ) : (
            // DRAG SOURCE: This container can be DRAGGED
            // - refDraggable makes this div draggable
            // - Only works when hasFoods is true (cannot drag an empty meal)
            // - Not assigned in viewMode
            <div className="day-nutrition" ref={viewMode ? null : refDraggable}>
              {/* 
                DRAG HANDLE: The visual icon that the user grabs to drag
                - handleRefDraggable: Connects this icon with the draggable container
                - attributes/listeners: Enables drag events on this element
                - role="div": Prevents automatic role="button" from being applied
                - Positioned in the top-right corner (via CSS)
                - Hidden in viewMode
              */}
              {!viewMode && (
                <div
                  className={`header-actions mobile-hidden ${isDragging ? "hidden!" : ""}`}
                >
                  <PasteMealComponent
                    meal={meal}
                    day={day}
                    query={query}
                    planMode={currentPlanMode}
                    week={week}
                    hasFoods={hasFoods}
                  />
                  <CopyComponent
                    type="meal"
                    meal={meal}
                    day={day}
                    query={query}
                    planMode={currentPlanMode}
                  />
                  <div
                    className="drag-icon cursor-grab"
                    ref={handleRefDraggable}
                    {...attributes}
                    {...listeners}
                    role="div"
                  >
                    <img
                      src="/img/new-icons/drag-outline.svg"
                      alt={t("athlete-portal:foodItem.dragAlt")}
                    />
                  </div>
                </div>
              )}
              <div className="wrap-nutrition">
                {grouped.map((group) => (
                  <GroupedMealContainer
                    key={group.meal._id}
                    group={group}
                    mealType={type}
                    day={day}
                    planMode={currentPlanMode}
                    week={week}
                    viewMode={viewMode}
                    query={query}
                    hideHeaderActions={onlyOneGroup}
                  />
                ))}
                {singles?.map((food, index) => {
                  const {
                    type: foodType,
                    quantityNumber,
                    unitOfMeasure,
                    foodId,
                    _id: foodEntryId,
                  } = food;
                  if (!foodId) return null;

                  const {
                    calories = 0,
                    carbs = 0,
                    fats = 0,
                    name,
                    proteins = 0,
                    quantityDescription,
                    brand,
                    _id: foodItemId,
                  } = foodId;

                  // Prefer entry _id so duplicate foods (same catalog food) are identified uniquely
                  const foodMealPlanId =
                    foodEntryId != null ? foodEntryId : foodItemId;

                  // Index in the full meal.foods array (backend expects this, not index in singles)
                  const indexInMealFoods = foods.findIndex((f) => f === food);
                  const foodIndexInMeal =
                    indexInMealFoods >= 0 ? indexInMealFoods : index;

                  return (
                    <FoodItem
                      key={`${index}-${foodMealPlanId}`}
                      name={name}
                      brand={brand}
                      quantityNumber={quantityNumber}
                      unitOfMeasure={unitOfMeasure}
                      quantityDescription={quantityDescription}
                      foodType={foodType}
                      proteins={proteins}
                      carbs={carbs}
                      fats={fats}
                      calories={calories}
                      food={food}
                      foodMealPlanId={foodMealPlanId}
                      foodIndex={foodIndexInMeal}
                      day={day}
                      mealType={type}
                      planMode={currentPlanMode}
                      week={week}
                      viewMode={viewMode}
                      query={query}
                      hideHeaderActions={onlyOneSingle}
                    />
                  );
                })}

                {hasMacrosTotal && (
                  <div className="resume">
                    <div className="protein-container">{totalProteins}</div>
                    <div className="carbs-container">{totalCarbs}</div>
                    <div className="fat-container">{totalFats}</div>
                    <div className="calories-container">{totalCalories}</div>
                  </div>
                )}
              </div>
              {!viewMode && (
                <div className={`action ${isDragging ? "hidden!" : ""}`}>
                  <a href={modalHref}>
                    <SvgIcon src={`/img/new-icons/edit-pencil.svg`} />
                    <span>{t("common:edit")}</span>
                  </a>
                  <button onClick={deleteFoodsOfMeal}>
                    <SvgIcon
                      src="/img/new-icons/red-trash.svg"
                      noFilterOnLight={true}
                    />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="day bg-(--footer-background-color)!"></div>
      )}
    </div>
  );
};

MealPlannerCalendar.propTypes = {
  query: PropTypes.object,
  day: PropTypes.object,
  meal: PropTypes.object,
  week: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewMode: PropTypes.bool,
  basicMealsInformation: PropTypes.array,
  planMode: PropTypes.string,
};
